<!-- =========================================================================================
  File Name: UserView.vue
  Description: User View page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div>
		<bread-crumbs
			:title="$t('SessionApi.SessionApi')"
			:sizeTitle="30"
			:lineHeightTitle="37"
			:sizeSubTitle="15"
			:lineHeightSubTitle="18"
		>
		</bread-crumbs>
		<div id="page-company-view">
			<vs-alert color="danger" title="User Not Found" :active.sync="company_not_found">
				<span>User record with id: {{ $route.params.userId }} not found. </span>
				<span>
					<span>Check </span
					><router-link :to="{name: 'page-user-list'}" class="text-inherit underline">All Users</router-link>
				</span>
			</vs-alert>

			<div id="user-data">
				<vx-card title="" class="mb-base" no-shadow>
					<session-api-company-data v-if="company" :item="company"></session-api-company-data>

					<vs-divider></vs-divider>

					<session-api-navbar :handleOption="handleOption" :selected="selected"></session-api-navbar>

					<session-api-list
						:handleItemSelected="handleItemSelected"
						:handleOption="handleOption"
						:clearSelectionTrigger="clearSelectionTrigger"
					></session-api-list>
				</vx-card>
			</div>

			<vs-prompt
				:title="promptTitle.toLowerCase() | title"
				:accept-text="update ? $t('Crud.Update') : $t('Crud.Create')"
				:cancel-text="$t('Cancel')"
				button-cancel="border"
				@cancel="clearFields"
				@accept="update ? updateSessionApi() : createNewSessionApi()"
				@close="clearFields"
				:active.sync="activePrompt"
				:is-valid="validateForm"
			>
				<form>
					<span v-if="update">
						{{ $t("SessionApi.AccessToken") }}: <br />
						<span class="text-sm font-semibold" v-if="selected">{{ selected.access_token }}</span>
					</span>
					<span v-else>{{ $t("SessionApi.GenerateAccessToken") }}</span>

					<div class="vx-row">
						<div class="vx-col w-full mt-2" v-show="!update">
							<label class="vs-input--label text-primary">{{
								$t("CompanyForm.AdminResource").toLowerCase() | title
							}}</label>
							<v-select
								label="username"
								:options="resources"
								:dir="$vs.rtl ? 'rtl' : 'ltr'"
								v-model="resource"
							/>
						</div>
						<div class="vx-col w-full mt-2 mb-base">
							<small class="date-label"> {{ expireDateLabelText.toLowerCase() | title }}</small>
							<datepicker
								:language="$vs.rtl ? langHe : langEn"
								name="expireDate"
								v-validate="'validatorExpireDate'"
								v-model="expireDate"
								:disabledDates="disabledDatesTo"
							></datepicker>
							<span class="text-danger text-sm" v-show="errors.has('expireDate')"
								>{{ $t("SessionApi.ExpireDateError") }}:
								<code>{{ company ? new Date(company.date_end).toLocaleString() : "" }}</code></span
							>
						</div>
					</div>
				</form>
			</vs-prompt>

			<vs-prompt
				:title="exportType === 'EXPORT' ? $t('ManualData.Download') + ' ' + 'Token' : $t('ExportAll')"
				class="export-options"
				@cancel="clearFields"
				@accept="exportToExcel"
				:accept-text="exportType === 'EXPORT' ? $t('ManualData.Download') : $t('ExportAll')"
				:cancel-text="$t('Cancel')"
				@close="clearFields"
				:active.sync="activeExportPrompt"
			>
				<vs-input v-model="fileName" placeholder="Enter File Name.." class="w-full" />
				<v-select v-model="selectedFormat" :options="formats" class="my-4" />
				<div class="flex">
					<span class="mr-4">{{ $t("MapModule.CellAutoWith") }}:</span>
					<vs-switch v-model="cellAutoWidth">{{ $t("MapModule.CellAutoWith") }}</vs-switch>
				</div>
			</vs-prompt>
		</div>
	</div>
</template>

<script>
// Components
import vSelect from "vue-select";
import Datepicker from "vuejs-datepicker";
import {en, es} from "vuejs-datepicker/src/locale";
import {Validator} from "vee-validate";

// Stores
import moduleCompany from "@/store/company/moduleCompany.js";
import moduleResource from "@/store/resource/moduleResource.js";

// Custom Components
import SessionApiList from "./SessionApiList.vue";
import SessionApiNavbar from "./SessionApiNavbar.vue";
import SessionApiCompanyData from "./SessionApiCompanyData.vue";

// Helpers
import DateUtils from "@/helpers/DateUtils.js";

export default {
	components: {
		vSelect,
		Datepicker,
		SessionApiList,
		SessionApiNavbar,
		SessionApiCompanyData,
	},
	data() {
		return {
			company_not_found: false,
			selected: null,
			company: null,
			clearSelectionTrigger: 0,
			activePrompt: false,
			resource: null,
			expireDate: new Date(),
			langHe: es,
			langEn: en,
			update: false,
			// properties to export
			fileName: "",
			formats: ["xlsx", "csv", "txt"],
			cellAutoWidth: true,
			selectedFormat: "xlsx",
			headerTitle: ["Token", this.$t("Email"), this.$t("Username"), this.$t("SessionApi.ExpireDate")],
			headerVal: ["access_token", "email", "username", "expire_token"],
			activeExportPrompt: false,
			exportType: "EXPORT",
		};
	},
	computed: {
		resources() {
			return this.$store.state.moduleResource.resources.filter((resource) => resource.role_name === "ADMIN");
		},
		validateForm() {
			return !this.errors.any() && this.resource !== null && Object.entries(this.resource).length > 0;
		},
		disabledDatesTo() {
			return {to: new Date()};
		},
		promptTitle() {
			return this.update
				? `${this.$t("Crud.Update")} ${this.$t("SessionApi.AccessToken")}`
				: `${this.$t("Crud.Create")} ${this.$t("SessionApi.AccessToken")}`;
		},
		expireDateLabelText() {
			return this.update
				? `${this.$t("Crud.Update")} ${this.$t("SessionApi.ExpireDate")}`
				: this.$t("SessionApi.ExpireDate");
		},
	},
	methods: {
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		handleItemSelected(val) {
			this.selected = val;
		},
		handleOption(option) {
			switch (option) {
				case "CREATE":
					this.update = false;
					this.activePrompt = true;
					break;
				case "UPDATE":
					this.activePrompt = true;
					this.update = true;
					this.resource = this.resources.find((resource) => resource.username === this.selected.username);
					this.expireDate = this.selected.expire_token;
					break;
				case "DELETE":
					this.confirmDeleteSessionApi();
					break;
				case "EXPORT":
					this.exportType = option;
					this.activeExportPrompt = true;
				case "EXPORT_ALL":
					this.exportType = option;
					this.activeExportPrompt = true;
					break;
				case "CLEAR_SELECTION":
					this.selected = null;
					this.resource = null;
					this.clearSelectionTrigger += 1;
					break;
				default:
					break;
			}
		},
		clearFields() {
			this.resource = null;
			this.update = false;
		},
		confirmDeleteSessionApi() {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: `${this.$t("Confirm")} ${this.$t("Crud.Delete")}`,
				text: `${this.$t("Crud.Delete")} token:  ${this.selected.access_token}`,
				accept: this.deleteSessionApi,
				acceptText: this.$t("Crud.Delete"),
				cancelText: this.$t("Cancel"),
			});
		},
		async deleteSessionApi() {
			try {
				this.openLoading();
				await this.$store.dispatch("moduleSessionApi/removeSessionApi", this.selected.access_token);
				this.closeLoading();
				this.$vs.notify({
					color: "success",
					title: this.$t("Record.DeleteRecord"),
					text: this.$t("Record.TextConfirmRecordDelete"),
				});
			} catch (error) {
				this.closeLoading();
				console.error(error);
			}
		},
		async getCompany() {
			const userInfo = JSON.parse(localStorage.getItem("userInfo"));
			const companyId = this.$route.params.id ? this.$route.params.id : +userInfo.datarutas_company_id;

			try {
				const response = await this.$store.dispatch("moduleCompany/getCompanyId", companyId);
				if (Array.isArray(response.data) && response.data.length > 0) {
					this.company = response.data[0];
					const visualConfig = this.$store.getters["moduleCompanyConfig/visualConfig"];
					if (visualConfig && visualConfig.config_json) {
						const {url_logo} = visualConfig.config_json;
						this.company.url_logo = url_logo ? url_logo : null;
					}
				} else {
					throw new Error("No company found");
				}
			} catch (error) {
				console.error(error);
				this.company_not_found = true;
			}
		},
		async createNewSessionApi() {
			const payload = {
				company_id: +this.$route.params.id,
				resource_id: this.resource.id,
				expire_token: DateUtils.toDefaultDateTime(this.expireDate, "YYYY-MM-DD"),
			};
			this.openLoading();
			try {
				await this.$store.dispatch("moduleSessionApi/createSessionsApi", payload);
				this.closeLoading();
				this.$vs.notify({
					color: "success",
					title: "User Deleted",
					text: "The selected user was successfully deleted",
				});
			} catch (error) {
				this.closeLoading();
				console.error(error);
			}
		},
		async updateSessionApi() {
			this.openLoading();
			try {
				const payload = {
					...this.selected,
					expire_token: DateUtils.toDefaultDateTime(this.expireDate, "YYYY-MM-DD"),
				};
				await this.$store.dispatch("moduleSessionApi/updateSessionApi", payload);
				this.handleOption("CLEAR_SELECTION");
				this.closeLoading();
				this.$vs.notify({
					color: "success",
					title: this.$t("Record.UpdateRecord"),
					text: this.$t("Record.TextConfirmRecordUpdate"),
				});
			} catch (error) {
				this.closeLoading();
				console.error(error);
			}
		},
		async getResources() {
			const payload = {
				order_by: "added_on",
				sort: "DESC",
				company_id: this.$route.params.id,
			};
			this.openLoading();
			try {
				await this.$store.dispatch("moduleResource/fetchResourceFilter", payload);
				this.closeLoading();
			} catch (error) {
				this.closeLoading();
			}
		},
		/**
		 * Custom validator to check allow max expire date
		 * @param {Date} value Current date value selected on the piker
		 * @param {*} args
		 */
		validatorExpireDate(value, args) {
			if (!value || value === "" || typeof value === "number") {
				return true;
			}
			const expDate = typeof value === "string" ? new Date(value).getTime() : value.getTime();
			const companyDate =
				typeof this.company.date_end === "string"
					? new Date(this.company.date_end).getTime()
					: this.company.date_end.getTime();
			return expDate <= companyDate;
		},
		exportToExcel() {
			import("@/vendor/Export2Excel").then((excel) => {
				const list =
					this.exportType === "EXPORT"
						? [this.selected]
						: this.$store.getters["moduleSessionApi/getAllSessions"]();
				const data = this.formatJson(this.headerVal, list);
				excel.export_json_to_excel({
					header: this.headerTitle,
					data,
					filename: this.fileName,
					autoWidth: this.cellAutoWidth,
					bookType: this.selectedFormat,
				});
				this.clearFields();
			});
		},
		formatJson(filterVal, jsonData) {
			return jsonData.map((v) =>
				filterVal.map((j) => {
					return v[j];
				})
			);
		},
		clearFields() {
			this.fileName = "";
			this.cellAutoWidth = true;
			this.selectedFormat = "xlsx";
		},
	},
	created() {
		// Register Module Company Module
		if (!moduleCompany.isRegistered) {
			this.$store.registerModule("moduleCompany", moduleCompany);
			moduleCompany.isRegistered = true;
		}

		if (!moduleResource.isRegistered) {
			this.$store.registerModule("moduleResource", moduleResource);
			moduleResource.isRegistered = true;
		}

		// Register custom validator
		Validator.extend("validatorExpireDate", this.validatorExpireDate);
		new Validator({trueField: "validatorExpireDate"});

		if (this.$route.params.company) {
			this.company = this.$route.params.company;
		} else {
			this.getCompany();
		}

		this.getResources();
	},
};
</script>

<style lang="scss">
#page-company-view {
	background-color: white;
	table {
		td {
			vertical-align: top;
			min-width: 140px;
			padding-bottom: 0.8rem;
			word-break: break-all;
		}

		&:not(.permissions-table) {
			td {
				@media screen and (max-width: 370px) {
					display: block;
				}
			}
		}
	}
}

// #account-info-col-1 {
//   // flex-grow: 1;
//   width: 30rem !important;
//   @media screen and (min-width:1200px) {
//     & {
//       flex-grow: unset !important;
//     }
//   }
// }

@media screen and (min-width: 1201px) and (max-width: 1211px),
	only screen and (min-width: 636px) and (max-width: 991px) {
	#account-info-col-1 {
		width: calc(100% - 12rem) !important;
	}

	// #account-manage-buttons {
	//   width: 12rem !important;
	//   flex-direction: column;

	//   > button {
	//     margin-right: 0 !important;
	//     margin-bottom: 1rem;
	//   }
	// }
}
</style>
